/**
 * Lightweight pub/sub primitive. Compose into services that need to notify
 * subscribers when their state changes (e.g. cache invalidation after mutations).
 *
 * ```ts
 * class MyService {
 *   private changes = new Subscribable<{ id: string }>();
 *
 *   public subscribe = this.changes.subscribe;
 *
 *   mutate(id: string) {
 *     // ...
 *     this.changes.notify({ id });
 *   }
 * }
 * ```
 */
export class Subscribable<T = void> {
  protected listeners = new Set<(value: T) => void>();

  /** Number of currently-registered listeners. */
  get size(): number {
    return this.listeners.size;
  }

  /** Registers `listener`. Returns an unsubscribe function. */
  subscribe = (listener: (value: T) => void): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  /** Invokes every registered listener with `value`. */
  notify = (value: T): void => {
    this.listeners.forEach((listener) => listener(value));
  };
}

/**
 * Subscribable that caches the most recently broadcast value and replays it
 * to any subscriber registering afterwards. Useful for state-of-the-world
 * channels where late joiners need the current value immediately, not just
 * future changes.
 *
 * Example:
 * ```ts
 * const state = new StatefulSubscribable<{ count: number }>();
 * state.notify({ count: 1 });
 * state.subscribe(s => console.log(s.count)); // logs 1 immediately
 * state.notify({ count: 2 });                 // logs 2
 * ```
 */
export class StatefulSubscribable<T> extends Subscribable<T> {
  private latest: { value: T } | null = null;

  get value(): T | undefined {
    return this.latest?.value;
  }

  subscribe = (listener: (value: T) => void): (() => void) => {
    this.listeners.add(listener);
    if (this.latest) listener(this.latest.value);
    return () => {
      this.listeners.delete(listener);
    };
  };

  notify = (value: T): void => {
    this.latest = { value };
    this.listeners.forEach((listener) => listener(value));
  };

  reset = (): void => {
    this.latest = null;
  };
}
